import React, { useEffect, useState } from 'react';
import { hasProgrammeToken, programmeClientId, programmeRequest, setProgrammeToken } from './programmeApi';

let scriptPromise;

function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    }).catch(error => {
      scriptPromise = null;
      throw error;
    });
  }
  return scriptPromise;
}

function showValue(value) {
  return value === undefined || value === null ? 'Not returned' : String(value);
}

export default function ProgrammeCheck() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(hasProgrammeToken);

  useEffect(() => {
    let active = true;

    const checkSession = async () => {
      if (!hasProgrammeToken()) {
        setChecking(false);
        return;
      }
      setChecking(true);
      try {
        const result = await programmeRequest('/api/me');
        if (active) {
          setProfile(result);
          setError(null);
        }
      } catch (requestError) {
        if (active) {
          setProfile(null);
          setError({
            status: requestError?.status,
            message: requestError?.message || 'Request failed.',
          });
        }
      } finally {
        if (active) setChecking(false);
      }
    };

    checkSession();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (profile || checking) return undefined;
    let active = true;

    loadGoogle().then(() => {
      if (!active) return;
      window.google.accounts.id.initialize({
        client_id: programmeClientId(),
        callback: async ({ credential }) => {
          if (!active || !credential) return;
          setProgrammeToken(credential);
          setError(null);
          setChecking(true);
          try {
            const result = await programmeRequest('/api/me');
            if (active) setProfile(result);
          } catch (requestError) {
            if (active) {
              setError({
                status: requestError?.status,
                message: requestError?.message || 'Request failed.',
              });
            }
          } finally {
            if (active) setChecking(false);
          }
        },
      });
      const target = document.getElementById('programme-check-google-button');
      if (target) {
        target.replaceChildren();
        window.google.accounts.id.renderButton(target, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
        });
      }
    }).catch(loadError => {
      if (active) {
        setError({ status: undefined, message: loadError?.message || 'Google sign-in could not load.' });
      }
    });

    return () => { active = false; };
  }, [profile, checking]);

  if (profile) {
    return (
      <main className="min-h-screen bg-brand-light px-4 pt-28">
        <dl className="mx-auto max-w-md space-y-3 rounded-2xl border border-brand-dark bg-white p-8 text-sm">
          <div><dt className="font-bold">Email</dt><dd>{showValue(profile?.email)}</dd></div>
          <div><dt className="font-bold">Admin</dt><dd>{showValue(profile?.admin)}</dd></div>
          <div><dt className="font-bold">Rights admin</dt><dd>{showValue(profile?.rightsAdmin)}</dd></div>
        </dl>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-brand-light px-4 pt-28">
      <div className="mx-auto max-w-md rounded-2xl border border-brand-dark bg-white p-8">
        {!checking && <div id="programme-check-google-button" />}
        {checking && <p role="status">Checking sign-in…</p>}
        {error && (
          <p role="alert" className="mt-4 text-sm text-red-600">
            HTTP status: {error.status || 'unavailable'} — {error.message}
          </p>
        )}
      </div>
    </main>
  );
}